import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SiFacebook, SiInstagram } from "react-icons/si";
import { AlertTriangle, CheckCircle2, Type, Hash, Image as ImageIcon, RotateCcw, Pencil, Eye, Heart, MessageCircle, Send, Bookmark, MoreHorizontal, ThumbsUp, Share2, Globe, ShoppingBag, ExternalLink, Link2, ChevronUp } from "lucide-react";
import eatzyLogo from "@/assets/eatzy-teal-disc-1024.png";

interface TransformedContent {
  text: string;
  hashtags: string[];
  mediaUrls: string[];
  callToAction?: string;
  platform: string;
  characterCount: number;
  characterLimit: number;
  isWithinLimit: boolean;
  warnings: string[];
  formattedPreview: string;
}

interface PreviewResponse {
  previews: Record<string, TransformedContent>;
  invalidPlatforms: string[];
}

interface SocialMediaPreviewProps {
  content: {
    text: string;
    title?: string;
    type?: string;
    link?: string;
  };
  platforms: string[];
  restaurantName?: string;
  imageUrl?: string;
  videoUrl?: string;
  platformOverrides?: Record<string, string>;
  hashtagsInFirstComment?: boolean;
  onContentChange?: (platform: string, newText: string) => void;
  onPreviewsLoaded?: (previews: Record<string, TransformedContent>) => void;
  onReset?: (platform: string, originalText: string) => void;
}

export function SocialMediaPreview({
  content, platforms, restaurantName, imageUrl, videoUrl,
  platformOverrides, hashtagsInFirstComment = false, onContentChange, onPreviewsLoaded, onReset,
}: SocialMediaPreviewProps) {
  const [previews, setPreviews] = useState<Record<string, TransformedContent>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('eatzy');
  const [userEdited, setUserEdited] = useState<Record<string, boolean>>({});
  const [editMode, setEditMode] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!content.text || platforms.length === 0) {
      setPreviews({});
      return;
    }

    const fetchPreviews = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch('/api/partner/social/transform-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content,
            platforms,
            restaurantName,
            firstCommentHashtags: hashtagsInFirstComment,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to generate preview');
        }

        const data: PreviewResponse = await response.json();
        setPreviews(data.previews);
        setUserEdited({});
        onPreviewsLoaded?.(data.previews);
        
        if (data.invalidPlatforms.length > 0) {
          setError(`Invalid platforms: ${data.invalidPlatforms.join(', ')}`);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchPreviews, 500);
    return () => clearTimeout(debounceTimer);
  }, [content.text, content.title, content.type, content.link, platforms.join(','), restaurantName, hashtagsInFirstComment]);

  useEffect(() => {
    const allAvailable = ['eatzy', ...platforms.filter(p => p !== 'eatzy')];
    if (!allAvailable.includes(activeTab)) {
      setActiveTab(allAvailable[0]);
    }
  }, [platforms, activeTab]);

  const handleTextChange = useCallback((platform: string, newText: string) => {
    setUserEdited(prev => ({ ...prev, [platform]: true }));
    onContentChange?.(platform, newText);
  }, [onContentChange]);

  const handleReset = useCallback((platform: string) => {
    const preview = previews[platform];
    if (preview) {
      setUserEdited(prev => ({ ...prev, [platform]: false }));
      onReset?.(platform, preview.formattedPreview);
    }
  }, [previews, onReset]);

  if (platforms.length === 0) {
    return null;
  }

  const allTabs = ['eatzy', ...platforms.filter(p => p !== 'eatzy')];

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
        return <SiFacebook className="h-4 w-4 text-[#1877F2]" />;
      case 'instagram':
        return <SiInstagram className="h-4 w-4 text-[#E4405F]" />;
      case 'eatzy':
        return <img src={eatzyLogo} alt="" className="h-4 w-4 rounded-full" />;
      default:
        return null;
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Eye className="h-4 w-4" />
          Preview how your post will appear
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              {allTabs.map((platform) => (
                <TabsTrigger key={platform} value={platform} className="gap-2">
                  {getPlatformIcon(platform)}
                  <span className="capitalize">{platform}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="eatzy" className="mt-0">
              <div className="space-y-3">
                {!(editMode['eatzy'] ?? false) && (
                  <EatzyPostMockup
                    caption={content.text
                      .replace(/#\w+/g, '')
                      .replace(/https?:\/\/\S+/g, '')
                      .replace(/\n{3,}/g, '\n\n')
                      .trim()}
                    imageUrl={imageUrl}
                    videoUrl={videoUrl}
                    restaurantName={restaurantName}
                  />
                )}
                <div className="flex items-center justify-end">
                  <Button
                    size="sm"
                    variant={(editMode['eatzy'] ?? false) ? "secondary" : "ghost"}
                    className="h-6 px-2 text-xs gap-1"
                    onClick={() => setEditMode(prev => ({ ...prev, eatzy: !prev.eatzy }))}
                  >
                    {(editMode['eatzy'] ?? false) ? <Eye className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                    {(editMode['eatzy'] ?? false) ? 'Preview' : 'Edit'}
                  </Button>
                </div>
                {(editMode['eatzy'] ?? false) && (
                  <Textarea
                    value={content.text}
                    onChange={(e) => onContentChange?.('eatzy', e.target.value)}
                    className="min-h-[120px] resize-none text-sm leading-relaxed"
                    data-testid="preview-textarea-eatzy"
                  />
                )}
              </div>
            </TabsContent>

            {platforms.filter(p => p !== 'eatzy').map((platform) => {
              const preview = previews[platform];
              if (!preview) return null;

              const displayText = platformOverrides?.[platform] ?? preview.formattedPreview;
              const charCount = displayText.length;
              const charLimit = preview.characterLimit;
              const isOverLimit = charCount > charLimit;
              const isEdited = userEdited[platform] && displayText !== preview.formattedPreview;
              const isEditing = editMode[platform] ?? false;

              return (
                <TabsContent key={platform} value={platform} className="mt-0">
                  <div className="space-y-3">
                    {platform === 'instagram' && (
                      <InstagramMockup
                        displayText={displayText}
                        imageUrl={imageUrl}
                        videoUrl={videoUrl}
                        restaurantName={restaurantName}
                        hashtagsInFirstComment={hashtagsInFirstComment}
                      />
                    )}
                    {platform === 'facebook' && (
                      <FacebookMockup
                        displayText={displayText}
                        imageUrl={imageUrl}
                        videoUrl={videoUrl}
                        restaurantName={restaurantName}
                        link={content.link}
                      />
                    )}

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          <Type className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className={`text-xs ${isOverLimit ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                            {charCount.toLocaleString()} / {charLimit.toLocaleString()}
                          </span>
                          {isOverLimit ? (
                            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {preview.hashtags.length} hashtag{preview.hashtags.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {isEdited && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs gap-1 text-muted-foreground"
                            onClick={() => handleReset(platform)}
                          >
                            <RotateCcw className="h-3 w-3" />
                            Reset
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={isEditing ? "secondary" : "ghost"}
                          className="h-6 px-2 text-xs gap-1"
                          onClick={() => setEditMode(prev => ({ ...prev, [platform]: !prev[platform] }))}
                        >
                          {isEditing ? <Eye className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                          {isEditing ? 'Preview' : 'Edit'}
                        </Button>
                      </div>
                    </div>

                    {isEditing && (
                      <Textarea
                        value={displayText}
                        onChange={(e) => handleTextChange(platform, e.target.value)}
                        className="min-h-[120px] resize-none text-sm leading-relaxed"
                        data-testid={`preview-textarea-${platform}`}
                      />
                    )}

                    {!imageUrl && !videoUrl && platform === 'instagram' && (
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        <ImageIcon className="h-3 w-3 mr-1" />
                        Image required for Instagram
                      </Badge>
                    )}

                    {preview.warnings.length > 0 && (
                      <div className="space-y-1">
                        {preview.warnings.map((warning, idx) => (
                          <Alert key={idx} className="py-2 bg-amber-50/50 border-amber-200">
                            <AlertTriangle className="h-3 w-3 text-amber-600" />
                            <AlertDescription className="text-xs text-amber-700">
                              {warning}
                            </AlertDescription>
                          </Alert>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}

function renderCaptionWithHashtags(text: string) {
  const parts = text.split(/(#[\w\u00C0-\u024F]+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('#')) {
      return <span key={i} className="text-[#00376B] dark:text-blue-400">{part}</span>;
    }
    return <span key={i}>{part}</span>;
  });
}

export function InstagramMockup({ displayText, imageUrl, videoUrl, restaurantName, hashtagsInFirstComment = false }: {
  displayText: string;
  imageUrl?: string;
  videoUrl?: string;
  restaurantName?: string;
  hashtagsInFirstComment?: boolean;
}) {
  const handle = restaurantName
    ? '@' + restaurantName.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20)
    : '@restaurant';

  // Strip hashtag lines from caption for first-comment preview mode
  const captionForPreview = hashtagsInFirstComment
    ? displayText.split('\n').filter(line => !line.trim().startsWith('#')).join('\n').trimEnd()
    : displayText;

  // Extract hashtags to show as a comment preview
  const hashtagLine = hashtagsInFirstComment
    ? displayText.split('\n').filter(line => line.trim().startsWith('#')).join(' ')
    : '';

  return (
    <div className="rounded-lg border bg-white dark:bg-zinc-950 overflow-hidden">
      <div className="flex items-center gap-2.5 p-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E4405F] to-[#FCAF45] p-[2px]">
          <div className="w-full h-full rounded-full bg-white dark:bg-zinc-950 flex items-center justify-center">
            <span className="text-[10px] font-bold">{(restaurantName || 'R')[0].toUpperCase()}</span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate text-foreground">{handle}</p>
        </div>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </div>

      {videoUrl ? (
        <div className="aspect-square bg-muted overflow-hidden">
          <video src={videoUrl} autoPlay muted loop playsInline className="w-full h-full object-cover" />
        </div>
      ) : imageUrl ? (
        <div className="aspect-square bg-muted overflow-hidden">
          <img src={imageUrl} alt="Post preview" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-square bg-muted flex items-center justify-center">
          <ImageIcon className="h-12 w-12 text-muted-foreground/40" />
        </div>
      )}

      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Heart className="h-5 w-5 text-foreground" />
            <MessageCircle className="h-5 w-5 text-foreground" />
            <Send className="h-5 w-5 text-foreground" />
          </div>
          <Bookmark className="h-5 w-5 text-foreground" />
        </div>
        <p className="text-xs font-semibold text-foreground">0 likes</p>
        <div className="text-xs leading-relaxed text-foreground">
          <span className="font-semibold mr-1">{handle}</span>
          {renderCaptionWithHashtags(captionForPreview)}
        </div>
        {hashtagsInFirstComment && hashtagLine && (
          <div className="border-t pt-2 mt-1">
            <p className="text-[10px] text-muted-foreground mb-1">First comment</p>
            <div className="text-xs leading-relaxed">
              <span className="font-semibold mr-1 text-foreground">{handle}</span>
              {renderCaptionWithHashtags(hashtagLine)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function FacebookMockup({ displayText, imageUrl, videoUrl, restaurantName, link }: {
  displayText: string;
  imageUrl?: string;
  videoUrl?: string;
  restaurantName?: string;
  link?: string;
}) {
  const name = restaurantName || 'Restaurant';
  let linkDomain = '';
  try { if (link) linkDomain = new URL(link).hostname; } catch { linkDomain = 'eatzy.app'; }

  const hasMedia = !!(videoUrl || imageUrl);

  return (
    <div className="rounded-lg border bg-white dark:bg-zinc-950 overflow-hidden">
      <div className="flex items-center gap-2.5 p-3">
        <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center">
          <span className="text-sm font-bold text-white">{name[0].toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{name}</p>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>Just now</span>
            <span>·</span>
            <Globe className="h-3 w-3" />
          </div>
        </div>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </div>

      <div className="px-3 pb-2">
        <p className="text-sm leading-relaxed text-foreground whitespace-pre-line">
          {renderCaptionWithHashtags(displayText)}
        </p>
      </div>

      {hasMedia ? (
        <div className="aspect-video bg-muted overflow-hidden relative">
          {videoUrl ? (
            <video src={videoUrl} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          ) : (
            <img src={imageUrl} alt="Post preview" className="w-full h-full object-cover" />
          )}
          {link && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center px-3">
              <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-xl px-3 py-2 flex items-center gap-2 max-w-[190px]">
                <ShoppingBag className="h-3.5 w-3.5 text-white shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[10px] font-semibold truncate">See Menu</p>
                  <p className="text-white/70 text-[9px] truncate">{linkDomain}</p>
                </div>
                <ChevronUp className="h-3 w-3 text-white/80 shrink-0" />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-video bg-muted flex items-center justify-center relative">
          <ImageIcon className="h-12 w-12 text-muted-foreground/40" />
          {link && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center px-3">
              <div className="bg-foreground/10 backdrop-blur-md border border-foreground/20 rounded-xl px-3 py-2 flex items-center gap-2 max-w-[190px]">
                <ShoppingBag className="h-3.5 w-3.5 text-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-foreground text-[10px] font-semibold truncate">See Menu</p>
                  <p className="text-muted-foreground text-[9px] truncate">{linkDomain}</p>
                </div>
                <ChevronUp className="h-3 w-3 text-muted-foreground shrink-0" />
              </div>
            </div>
          )}
        </div>
      )}

      {link && (
        <div className="mx-3 my-2 rounded-md border bg-muted/50 overflow-hidden">
          <div className="p-2.5">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{linkDomain}</p>
            <p className="text-sm font-semibold text-foreground mt-0.5">Order from {name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Tap to view menu and order for pickup or delivery</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-around py-2 px-3 border-t">
        <button className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
          <ThumbsUp className="h-4 w-4" /> Like
        </button>
        <button className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
          <MessageCircle className="h-4 w-4" /> Comment
        </button>
        <button className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium">
          <Share2 className="h-4 w-4" /> Share
        </button>
      </div>
    </div>
  );
}

export function InstagramStoryMockup({ imageUrl, videoUrl, restaurantName, link }: {
  imageUrl?: string;
  videoUrl?: string;
  restaurantName?: string;
  link?: string;
}) {
  const handle = restaurantName
    ? '@' + restaurantName.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20)
    : '@restaurant';

  let linkDomain = 'eatzykudoz.com';
  try { if (link) linkDomain = new URL(link).hostname; } catch { linkDomain = 'eatzykudoz.com'; }

  return (
    <div className="rounded-lg border bg-zinc-900 overflow-hidden" style={{ maxWidth: 240, margin: '0 auto' }}>
      <div className="relative" style={{ aspectRatio: '9/16' }}>
        {videoUrl ? (
          <video src={videoUrl} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
        ) : imageUrl ? (
          <img src={imageUrl} alt="Story preview" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-800 to-zinc-900 flex items-center justify-center">
            <ImageIcon className="h-10 w-10 text-zinc-600" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />

        <div className="absolute top-3 left-3 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E4405F] to-[#FCAF45] p-[2px]">
            <div className="w-full h-full rounded-full bg-zinc-900 flex items-center justify-center">
              <span className="text-[8px] font-bold text-white">{(restaurantName || 'R')[0].toUpperCase()}</span>
            </div>
          </div>
          <span className="text-white text-[11px] font-semibold drop-shadow-sm">{handle}</span>
        </div>

        <div className="absolute bottom-4 left-0 right-0 flex justify-center px-3">
          <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-xl px-3 py-2 flex items-center gap-2 w-full max-w-[190px]">
            <Link2 className="h-3.5 w-3.5 text-white shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-[10px] font-semibold truncate">See Menu</p>
              <p className="text-white/70 text-[9px] truncate">{linkDomain}</p>
            </div>
            <ChevronUp className="h-3 w-3 text-white/80 shrink-0" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function EatzyPostMockup({ caption, imageUrl, videoUrl, restaurantName, selectedItemName }: {
  caption: string;
  imageUrl?: string | null;
  videoUrl?: string | null;
  restaurantName?: string;
  selectedItemName?: string;
}) {
  return (
    <div className="rounded-lg border bg-white dark:bg-zinc-950 overflow-hidden">
      <div className="flex items-center gap-2.5 p-3">
        <div className="w-9 h-9 rounded-full overflow-hidden bg-[#0D9C97] flex items-center justify-center">
          <img src={eatzyLogo} alt="Eatzy" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{restaurantName || 'Restaurant'}</p>
          <p className="text-[11px] text-muted-foreground">Just now</p>
        </div>
      </div>

      {videoUrl ? (
        <div className="aspect-video bg-muted overflow-hidden relative">
          <video src={videoUrl} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          {selectedItemName && (
            <div className="absolute bottom-2 left-2">
              <Badge className="bg-white/90 text-foreground text-xs backdrop-blur-sm border-0 gap-1">
                <ShoppingBag className="h-3 w-3" />
                {selectedItemName}
              </Badge>
            </div>
          )}
        </div>
      ) : imageUrl ? (
        <div className="aspect-video bg-muted overflow-hidden relative">
          <img src={imageUrl} alt="Post preview" className="w-full h-full object-cover" />
          {selectedItemName && (
            <div className="absolute bottom-2 left-2">
              <Badge className="bg-white/90 text-foreground text-xs backdrop-blur-sm border-0 gap-1">
                <ShoppingBag className="h-3 w-3" />
                {selectedItemName}
              </Badge>
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-video bg-muted flex items-center justify-center">
          <ImageIcon className="h-12 w-12 text-muted-foreground/40" />
        </div>
      )}

      <div className="p-3 space-y-2.5">
        <p className="text-sm leading-relaxed text-foreground">{caption}</p>
        <Button size="sm" className="w-full bg-[#0D9C97] hover:bg-[#0B8A85] text-white gap-2">
          <ExternalLink className="h-3.5 w-3.5" />
          Order Now
        </Button>
      </div>
    </div>
  );
}
